import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Divider,
useTheme,
Menu,
Card,
CardContent,
Radio,
RadioGroup,
FormControlLabel,
FormLabel,
InputAdornment,
CardHeader
} from '@mui/material';
import {
Delete,
Person,
Search,
Add as AddIcon,
Close as CloseIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
PictureAsPdf,
TableChart,
GetApp,
X,
Email,
Phone,
LocationOn,
Cake,
Work,
Flag,
Badge
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
APIBrancheNamesFilter,
APIGuestStore,
APIGuestUpdate,
APIGuestDelete
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
formatCreatedAtDate,
formatNumberWithComma,
formatNumberWithComma2,
getCategoriesNameById,
getStatusById,
getUserDetails,
recordsPerPage,
statusData
} from '../../utils/helpers';
import {
exportGuestsToCSV,
exportGuestsToExcel,
exportGuestsToPDF,
} from '../../services/GuestService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import { guestApiService } from '../../apiServices/GuestApiService';

const Guest = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);
const [pageState, setPageState] = useState({
    pageData: []
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    dob: '',
    profession: '',
    nationaliti: 'native',
    national_id: '',
    nationalitycon: '',
    passport_no: '',
    visa_reg_no: '',
    purpose: 'Tourist',
    address: '',
    status: '1'
});

const [filters, setFilters] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    nationaliti: '',
    status: '',
    dateFrom: '',
    dateTo: ''
});
const [errors, setErrors] = useState({});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

    useEffect(() => {
        guestApiService.setToastFunction(showToast);
    }, [showToast]);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
    fetchBranchNames();
}, []);

const fetchBranchNames = async () => {
    try {
    const response = await axios.get(APIBrancheNamesFilter);
    if (response.data.status === 'success') {
        setBranchNamesFilterData(response.data.data || []);
    }
    } catch (error) {
    console.error("Error fetching branch names:", error);
    showToast("Error", "Failed to load branch names", "error");
    }
};

    const fetchData = async (branchId = null) => {
        setLoading(true);
        setError(null);

        try {
            // For super admin, use selected branch or current user's branch
            let branchIdToUse = branchId;
            if (!hasPermission(PERMISSIONS.BRANCHES)) {
                branchIdToUse = getUserDetails("branchId");
            }

            // Use the guestApiService to fetch data
            const response = await guestApiService.list(branchIdToUse);
            
            if (response.success) {
                const guests = Array.isArray(response.data) ? response.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: guests
                }));
                showToast("Success", response.message || TEXT_DATA_LOADED, "success");
            } else {
                setError(response.message);
                showToast("Error", response.message, "error");
            }
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            } else {
                errorMessage = error.message || errorMessage;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
};

// Helper functions
const getNationalityDisplay = (nationaliti) => {
    return nationaliti === 'native' ? 'Native' : 'Foreigner';
};

const getPurposeDisplay = (purpose) => {
    switch(purpose) {
    case 'Tourist': return 'Tourist';
    case 'Business': return 'Business';
    case 'Official': return 'Official';
    default: return purpose;
    }
};

// Filter based on search term and filters
const filteredData = pageState.pageData.filter(record => {
    const matchesSearch = searchTerm === '' ||
    record.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.phone.includes(searchTerm) ||
    record.status.toString().includes(searchTerm);

    const matchesFirstName = !filters.firstname ||
    record.firstname.toLowerCase().includes(filters.firstname.toLowerCase());

    const matchesLastName = !filters.lastname ||
    record.lastname.toLowerCase().includes(filters.lastname.toLowerCase());

    const matchesEmail = !filters.email ||
    record.email.toLowerCase().includes(filters.email.toLowerCase());

    const matchesPhone = !filters.phone ||
    record.phone.includes(filters.phone);

    const matchesNationaliti = !filters.nationaliti ||
    record.nationaliti === filters.nationaliti;

    const matchesStatus = !filters.status ||
    record.status.toString().includes(filters.status);

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate <= filterToDate;
    }

    return matchesSearch && matchesFirstName && matchesLastName && matchesEmail && 
        matchesPhone && matchesNationaliti && matchesStatus && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    dob: '',
    profession: '',
    nationaliti: 'native',
    national_id: '',
    nationalitycon: '',
    passport_no: '',
    visa_reg_no: '',
    purpose: 'Tourist',
    address: '',
    status: '1'
    });
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");

    // Set form data
    setFormData({
    firstname: record.firstname || '',
    lastname: record.lastname || '',
    email: record.email || '',
    phone: record.phone || '',
    dob: record.dob || '',
    profession: record.profession || '',
    nationaliti: record.nationaliti || 'native',
    national_id: record.national_id || '',
    nationalitycon: record.nationalitycon || '',
    passport_no: record.passport_no || '',
    visa_reg_no: record.visa_reg_no || '',
    purpose: record.purpose || 'Tourist',
    address: record.address || '',
    status: record.status || '1'
    });
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Reset error for this field
    setErrors(prev => ({
    ...prev,
    [name]: false
    }));

    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    setFilterOptions(false);
};

const clearFilters = () => {
    setFilters({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    nationaliti: '',
    status: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    setFilterOptions(false);
};

// Export handlers
const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
const handleExportClose = () => { setExportAnchorEl(null); };

const handleExportExcel = async () => {
    try {
        handleExportClose();
        await exportGuestsToExcel(filteredData, {
            firstname: filters.firstname,
            lastname: filters.lastname,
            email: filters.email,
            phone: filters.phone,
            nationaliti: filters.nationaliti,
            status: filters.status,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo
        });
        showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
        console.error('Excel export error:', error);
        showToast("Error", error.message, "error");
    }
};


const handleExportPDF = async () => {
    try {
        handleExportClose();
        await exportGuestsToPDF(filteredData, {
            firstname: filters.firstname,
            lastname: filters.lastname,
            email: filters.email,
            phone: filters.phone,
            nationaliti: filters.nationaliti,
            status: filters.status,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo
        });
        showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
        console.error('PDF export error:', error);
        showToast("Error", error.message, "error");
    }
};
const handleExportCSV = async () => {
    try {
        handleExportClose();
        await exportGuestsToCSV(filteredData);
        showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
        console.error('CSV export error:', error);
        showToast("Error", error.message, "error");
    }
};

// Get selected branch name
const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['firstname', 'lastname', 'email', 'phone', 'nationaliti'];
    
    requiredFields.forEach(field => {
    if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = true;
    }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    newErrors.email = true;
    }

    // Phone validation (basic)
    if (formData.phone && !/^\d+$/.test(formData.phone)) {
    newErrors.phone = true;
    }

    // Foreigner validation
    if (formData.nationaliti === 'foreigner') {
    const foreignerFields = ['nationalitycon', 'passport_no', 'visa_reg_no', 'purpose'];
    foreignerFields.forEach(field => {
        if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = true;
        }
    });
    }

    if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    showToast("Validation Error", "Please fill all required fields correctly", "warning");
    return false;
    }
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    dob: '',
    profession: '',
    nationaliti: 'native',
    national_id: '',
    nationalitycon: '',
    passport_no: '',
    visa_reg_no: '',
    purpose: 'Tourist',
    address: '',
    status: '1'
    });
};

const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form for ADD and UPDATE operations
    if (postStatus !== "DELETE") {
        if (!validateForm()) return;
    }

    // Create FormData object
    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));
    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
        formDataToSend.append('branch_id', selectedBranch);
    } else {
        formDataToSend.append('branch_id', getUserDetails("branchId"));
    }
    
    // Append all form data
    Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
    });

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
    }

    try {
        let response;

        // Call appropriate service method based on operation
        if (postStatus === "ADD") {
            response = await guestApiService.create(formDataToSend);
        } else if (postStatus === "UPDATE") {
            response = await guestApiService.update(formDataToSend);
        } else if (postStatus === "DELETE") {
            response = await guestApiService.delete(formDataToSend);
        }

        // Check success based on the service response
        if (response.success) {
            showToast("Success", response.message, "success");
            handleFormDataReset();
            handleDialogClose();
            fetchData();
        } else {
            showToast("Error", response.message, "error");
        }
    } catch (error) {
        // Fallback error handling
        showToast("Error", "An unexpected error occurred \n\n" + error.message, "error");
    }
};

const handleSubmit2 = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    
    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
    formDataToSend.append('branch_id', selectedBranch);
    } else {
    formDataToSend.append('branch_id', getUserDetails("branchId"));
    }
    
    // Append all form data
    Object.keys(formData).forEach(key => {
    formDataToSend.append(key, formData[key]);
    });

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
    formDataToSend.append('updateId', updateId);
    }

    try {
    let apiUrl, method;
    if (postStatus === "ADD") {
        apiUrl = APIGuestStore;
        method = 'post';
    } else if (postStatus === "UPDATE") {
        apiUrl = APIGuestUpdate;
        method = 'post';
    } else if (postStatus === "DELETE") {
        apiUrl = APIGuestDelete;
        method = 'post';
    }

    const response = await axios({
        method,
        url: apiUrl,
        data: formDataToSend
    });

    if (response.data.status === "success") {
        showToast("Success", response.data.message, "success");
        handleFormDataReset();
        handleDialogClose();
        fetchData();
    } else {
        showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
    }
    } catch (error) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
        const errors = data.errors || {};
        let errorMessages = [];
        for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
        }
        showToast(
            "Validation Error",
            errorMessages.join('\n') || "Please correct the form errors",
            "error"
        );
        } else if (status === 409) {
        showToast("Error", data.message || "This record already exists", "error");
        } else if (status >= 500) {
        showToast("Server Error", data.message || "Internal server error", "error");
        } else {
        showToast("Error", data.message || "Request failed", "error");
        }
    } else if (error.request) {
        showToast("Network Error", "No response from server. Please check your connection.", "error");
    } else {
        showToast("Error", error.message || "An unexpected error occurred", "error");
    }
    console.error("API Error:", error);
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Guest Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
    </Typography>
    
    {/* Display selected branch name */}
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        flexWrap: 'wrap' 
        }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Add New Guest
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Select Branch
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={() => setFilterOptions(true)}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
                size="small"
            >
                Export Data
            </Button>
            )}
            
            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            size="small"
            >
            Refresh
            </Button>
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search Guests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {(filters.firstname || filters.lastname || filters.email || filters.phone || 
        filters.nationaliti || filters.dateFrom || filters.dateTo || filters.status) && (
        <Box sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.firstname && (
            <Chip
                label={`First Name: ${filters.firstname}`}
                onDelete={() => setFilters(prev => ({ ...prev, firstname: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.lastname && (
            <Chip
                label={`Last Name: ${filters.lastname}`}
                onDelete={() => setFilters(prev => ({ ...prev, lastname: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.email && (
            <Chip
                label={`Email: ${filters.email}`}
                onDelete={() => setFilters(prev => ({ ...prev, email: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.phone && (
            <Chip
                label={`Phone: ${filters.phone}`}
                onDelete={() => setFilters(prev => ({ ...prev, phone: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.nationaliti && (
            <Chip
                label={`Nationality: ${getNationalityDisplay(filters.nationaliti)}`}
                onDelete={() => setFilters(prev => ({ ...prev, nationaliti: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateFrom && (
            <Chip
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            {filters.dateTo && (
            <Chip
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
                size="small"
            />
            )}
            <Button
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ minWidth: 'auto' }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Nationality</TableCell>
                <TableCell>Purpose</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((guest, index) => (
                <TableRow
                    key={index}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>{formatCreatedAtDate(guest.created_at)}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                    {guest.firstname} {guest.lastname}
                    </TableCell>
                    <TableCell>
                    <Box display="flex" alignItems="center">
                        <Phone sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                        {guest.phone}
                    </Box>
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={getNationalityDisplay(guest.nationaliti)}
                        color={guest.nationaliti === 'native' ? 'success' : 'warning'}
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {guest.purpose ? (
                        <Chip
                        label={getPurposeDisplay(guest.purpose)}
                        color="info"
                        size="small"
                        />
                    ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                    {guest.status === 1 ? (
                        <Chip
                        label={getStatusById(guest.status)}
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                        />
                    ) : (
                        <Chip
                        label={getStatusById(guest.status)}
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                        />
                    )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                    <TableCell>
                        <Chip
                        label={getCategoriesNameById(guest.branch_id, branchNamesFilterData)}
                        color="info"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    )}
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(guest)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(guest)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>

                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(guest)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>
        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Search sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No guests found</Typography>
            <Typography variant="body2">Try adjusting your search query or select a branch</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {branchNamesFilterData.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter guests only from that branch. 
            Leave unselected to view all guests.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                fetchData(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={() => setFilterOptions(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Guests
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Guests
            </Button>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Name
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="First Name"
                name="firstname"
                value={filters.firstname}
                onChange={handleFilterChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="Last Name"
                name="lastname"
                value={filters.lastname}
                onChange={handleFilterChange}
                />
            </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Contact
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="Email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={filters.phone}
                onChange={handleFilterChange}
                />
            </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Nationality
            </Typography>

            <FormControl fullWidth>
            <FormLabel>Nationality</FormLabel>
            <RadioGroup
                name="nationaliti"
                value={filters.nationaliti}
                onChange={handleFilterChange}
                row
            >
                <FormControlLabel value="" control={<Radio />} label="All" />
                <FormControlLabel value="native" control={<Radio />} label="Native" />
                <FormControlLabel value="foreigner" control={<Radio />} label="Foreigner" />
            </RadioGroup>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Status
            </Typography>

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                autoComplete="off"
                onChange={handleFilterChange}
                label="Status"
                required
            >
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setFilterOptions(false)}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={() => setFilterOptions(false)}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        scroll="paper"
        sx={{
        '& .MuiDialog-paper': {
            maxHeight: '90vh',
            height: 'auto',
        }
        }}
    >
        <DialogTitle sx={{
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pr: 1
        }}>
        <Typography variant="h6" sx={{ fontWeight: 'semibold', flex: 1 }}>
            {postStatus === "ADD" ? "Add New Guest" : "Edit Guest"}
        </Typography>
        <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAdd}
            aria-label="close"
            size="small"
        >
            <X />
        </IconButton>
        </DialogTitle>
        <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit}>
            <Card sx={{ mb: 3 }}>
            <CardHeader
                title="Guest Information"
                avatar={<Person color="primary" />}
            />
            <CardContent>
                <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="First Name *"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    error={errors.firstname}
                    helperText={errors.firstname ? "First name is required" : ""}
                    required
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Person />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Last Name *"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    error={errors.lastname}
                    helperText={errors.lastname ? "Last name is required" : ""}
                    required
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    helperText={errors.email ? "Valid email is required" : ""}
                    required
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Email />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Phone *"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={errors.phone}
                    helperText={errors.phone ? "Phone number is required" : "Add prefix without + sign Example: (88)01840997***"}
                    required
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Phone />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Cake />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="Profession"
                    name="profession"
                    value={formData.profession}
                    onChange={handleInputChange}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Work />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <FormControl component="fieldset" required>
                    <FormLabel component="legend">Nationality *</FormLabel>
                    <RadioGroup
                        row
                        name="nationaliti"
                        value={formData.nationaliti}
                        onChange={handleInputChange}
                    >
                        <FormControlLabel value="native" control={<Radio />} label="Native" />
                        <FormControlLabel value="foreigner" control={<Radio />} label="Foreigner" />
                    </RadioGroup>
                    </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                    <TextField
                    fullWidth
                    label="National ID"
                    name="national_id"
                    value={formData.national_id}
                    onChange={handleInputChange}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <Badge />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                {/* Foreigner Information (Conditional) */}
                {formData.nationaliti === 'foreigner' && (
                    <>
                    <Grid item xs={12} sm={6}>
                        <TextField
                        fullWidth
                        label="Nationality *"
                        name="nationalitycon"
                        value={formData.nationalitycon}
                        onChange={handleInputChange}
                        error={errors.nationalitycon}
                        helperText={errors.nationalitycon ? "Nationality is required for foreigners" : ""}
                        required
                        InputProps={{
                            startAdornment: (
                            <InputAdornment position="start">
                                <Flag />
                            </InputAdornment>
                            ),
                        }}
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                        fullWidth
                        label="Passport No *"
                        name="passport_no"
                        value={formData.passport_no}
                        onChange={handleInputChange}
                        error={errors.passport_no}
                        helperText={errors.passport_no ? "Passport number is required for foreigners" : ""}
                        required
                        />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                        <TextField
                        fullWidth
                        label="Visa/Reg. No *"
                        name="visa_reg_no"
                        value={formData.visa_reg_no}
                        onChange={handleInputChange}
                        error={errors.visa_reg_no}
                        helperText={errors.visa_reg_no ? "Visa/Reg. No is required for foreigners" : ""}
                        required
                        />
                    </Grid>
                    
                    <Grid item xs={12}>
                        <FormControl component="fieldset" required>
                        <FormLabel component="legend">Purpose *</FormLabel>
                        <RadioGroup
                            row
                            name="purpose"
                            value={formData.purpose}
                            onChange={handleInputChange}
                        >
                            <FormControlLabel value="Tourist" control={<Radio />} label="Tourist" />
                            <FormControlLabel value="Business" control={<Radio />} label="Business" />
                            <FormControlLabel value="Official" control={<Radio />} label="Official" />
                        </RadioGroup>
                        </FormControl>
                    </Grid>
                    </>
                )}
                
                <Grid item xs={12}>
                    <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    InputProps={{
                        startAdornment: (
                        <InputAdornment position="start">
                            <LocationOn />
                        </InputAdornment>
                        ),
                    }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        label="Status"
                    >
                        {statusData.map((item, index) => (
                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                        ))}
                    </Select>
                    </FormControl>
                </Grid>
                </Grid>
            </CardContent>
            </Card>
        </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        py: 2,
        justifyContent: 'space-between'
        }}>
        <Box>
            <Button
            color="error"
            onClick={handleCloseAdd}
            variant="contained"
            sx={{ mr: 1 }}
            >
            Cancel
            </Button>
        </Box>
        <Box>
            <Button
            type="submit"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            >
            {postStatus === "ADD" ? "Add Guest" : "Update Guest"}
            </Button>
        </Box>
        </DialogActions>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Guest Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Box>
                <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {selectedRecord.firstname} {selectedRecord.lastname}
                </Typography>
                </Grid>
                <b/>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Email:</b>
                    <Email sx={{ mr: 1, fontSize: 16,marginLeft:5 }} />
                    {selectedRecord.email}
                </Typography>
                </Grid>
                <b/>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Phone:</b>
                    <Phone sx={{ mr: 1, fontSize: 16, marginLeft:5 }} />
                    {selectedRecord.phone}
                </Typography>
                </Grid>
                <b/>
                {selectedRecord.dob && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Date of Birth:</b>
                    <Cake sx={{ mr: 1, fontSize: 16,marginLeft:5 }} />
                    {formatCreatedAtDate(selectedRecord.dob)}
                    </Typography>
                </Grid>
                )}
                <br/>
                {selectedRecord.profession && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Profession:</b>
                    <Work sx={{ mr: 1, fontSize: 16,marginLeft:5 }} />
                    {selectedRecord.profession}
                    </Typography>
                </Grid>
                )}
                <br/>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Nationality:</b>
                    <Chip
                    label={getNationalityDisplay(selectedRecord.nationaliti)}
                    color={selectedRecord.nationaliti === 'native' ? 'success' : 'warning'}
                    size="small"
                    sx={{ mt: 0.5 }}
                    />
                </Typography>
                </Grid>
                <br/>
                {selectedRecord.national_id && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>National ID:</b>
                    <Badge sx={{ mr: 1, fontSize: 16,marginLeft:5 }} />
                    {selectedRecord.national_id}
                    </Typography>
                </Grid>
                )}
                <br/>
                {selectedRecord.nationaliti === 'foreigner' && (
                <>
                    <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Nationality:</b>
                        <Flag sx={{ mr: 1, fontSize: 16,marginLeft:5 }} />
                        {selectedRecord.nationalitycon}
                    </Typography>
                    </Grid>
                    <br/>
                    <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Passport No:</b>{selectedRecord.passport_no}</Typography>
                    </Grid>
                    <br/>
                    <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Visa/Reg. No:</b>{selectedRecord.visa_reg_no}</Typography>
                    </Grid>
                    <br/>
                    <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Purpose:</b>
                        <Chip
                        label={getPurposeDisplay(selectedRecord.purpose)}
                        color="info"
                        size="small"
                        sx={{ mt: 0.5 }}
                        />
                    </Typography>
                    </Grid>
                </>
                )}
                <br/>
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Status:</b>
                    {selectedRecord.status === 1 ? (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5 }}
                    />
                    ) : (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="error"
                        sx={{ mt: 0.5 }}
                    />
                    )}
                </Typography>
                </Grid>
                <br/>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2"><b>Branch:</b></Typography>
                    <Chip
                    label={getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData)}
                    color="info"
                    variant="outlined"
                    size="small"
                    sx={{ mt: 0.5 }}
                    />
                </Grid>
                )}
                <br/>
                {selectedRecord.address && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2"><b>Address:</b></Typography>
                    <Typography variant="body1" sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box display="flex" alignItems="flex-start">
                        <LocationOn sx={{ mr: 1, mt: 0.5}} />
                        {selectedRecord.address}
                    </Box>
                    </Typography>
                </Grid>
                )}
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Created Date:</b></Typography>
                <Typography variant="body1">{formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2"><b>Last Updated:</b></Typography>
                <Typography variant="body1">{formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                </Grid>
            </Box>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Guest</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this guest?
        </Typography>
        {selectedRecord && (
            <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {selectedRecord.firstname} {selectedRecord.lastname}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Email: {selectedRecord.email} | Phone: {selectedRecord.phone}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Nationality: {getNationalityDisplay(selectedRecord.nationaliti)}
            </Typography>
            {hasPermission(PERMISSIONS.BRANCHES) && (
                <Typography variant="body2" color="text.secondary">
                Branch: {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData)}
                </Typography>
            )}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default Guest;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});

